import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppIcons } from "./AppIcons";
import { AppFocusKeep, IFocusEl } from "./AppFocus";

// AppLayout - THE THREE-PANE SHELL ON A WIDE SCREEN (owner-approved design item 12, 2026-09-30: "Yes, 3 columns").
//
//   >= 1100px, once the archive is loaded, on every archive route (the chat list, Overview, Tasks, Revenue, Sent, a chat):
//     RAIL   the views (`AppRail`: Chats / Overview / Tasks / Revenue / Sent) - `#wac-lefttabs`, a 64px column;
//     LEFT   the chat list (All / Starred / Archived, the rows/cards switch, the rows, the message hits) - `#wac-left`;
//     CENTER the open conversation (the two-line header, the frame + composer card) - or, with no chat open, a calm
//            "Choose a chat" with the shortcut hint, the Today strip and "Ask about everyone"; Tasks / Overview /
//            Revenue / Sent draw here too - `#wac-center`;
//     RIGHT  the AI panel - the existing 360px column, still INSIDE the center's `.contact` (so ×/✦, the sheet below
//            1100px, `ai-on`, `AppAiPanelStore` and every id `AppKeys` reads are untouched).
//   Below 1100px nothing here runs: the app paints `#wac-app` exactly as before (the list route / the contact route).
//
// ⭐ OPENING A CHAT DOES NOT REDRAW THE LIST (`AppThreeCol.Show`): the shell is made once; later routes rewrite only the
// center and the tabs' line. The list keeps its scroll, the search box its text and caret, and a focused row its focus;
// the opened row gets `aria-current="page"` and loses its "N new" in place (its link element survives).
// ⭐ A LIST REDRAW THAT MUST HAPPEN (a live message moved a row, the search changed, the index was rebuilt) keeps the
// pane's scroll position and puts focus back on the same row (`AppThreeCol.RepaintList`) - it never touches the center.
// ⛔ ONE BREAKPOINT: `AppLayout.WideMin` here, `AppMain.AssistWideQuery`, `AppKeyHost.WideQuery` and app.css's media
// queries are pinned to it by `AppThreeCol.test.ts`.
// ⛔ No inline style (the CSP): classes and app.css. Nothing here sends (decision 9).

export class AppLayout {
    /** The width (CSS px) from which the app is three panes and the AI panel a column, not a sheet. */
    public static readonly WideMin: number = 1100;
    public static readonly WideQuery: string = "(min-width: 1100px)";
    public static readonly NarrowQuery: string = "(max-width: 1099px)";
}

export class AppThreeColRules {
    /**
     * The routes that need no archive (sign-up, sign-in, leaving, "Import a backup", the owner's Claude panel) - never
     * drawn in panes. ⚠ Pinned to `AppMain.SignUpRoute`, `AppAccount.*Route` and `AppClaudeToken.Route` by a test.
     */
    public static readonly SideRoutes: string[] = ["signup", "signin", "leave", "import", "claude"];
    /** The tab each archive route lights; anything else (a chat, a new chat, an unknown word) is the chat list's. */
    public static readonly OwnTabs: string[] = ["tasks", "money", "overview", "sent"];

    /** The first word of an address ("#c/People%2F..." -> "c"). DOCUMENTED EXCEPTION: pure. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static First = (Hash: string): string => {
        return nz(Hash).replace(/^#/, "").split("/")[0];
    };

    /** Is this an archive route (drawn in panes on a wide screen)? */
    public static IsShellRoute = (Hash: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppThreeColRules.SideRoutes.indexOf(AppThreeColRules.First(Hash)) < 0;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeColRules.IsShellRoute");
            return false;
        }
    };

    /** Three panes now? Only with the archive loaded, a wide window and an archive route. */
    public static Wanted = (Loaded: boolean, Wide: boolean, Hash: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return Loaded === true && Wide === true && AppThreeColRules.IsShellRoute(Hash) === true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeColRules.Wanted");
            return false;
        }
    };

    /** The tab the route lights ("chats" for the list, a chat and anything unknown). */
    public static TabFor = (Hash: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "chats"; }
        try {
            const Word: string = AppThreeColRules.First(Hash);
            if (AppThreeColRules.OwnTabs.indexOf(Word) >= 0) { return Word; }
            return "chats";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeColRules.TabFor");
            return "chats";
        }
    };

    /** What the list must be redrawn for (besides another index): another search. Same = only mark the open row. */
    public static ListSig = (Filter: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "list|" + nz(Filter);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeColRules.ListSig");
            return "";
        }
    };
}

/** The shell's markup. ⚠ The ids are the seam `AppThreeCol` and `AppMain` write into. */
export class AppThreeColView {
    public static readonly RootId: string = "wac-3col";
    public static readonly LeftId: string = "wac-left";
    public static readonly TabsId: string = "wac-lefttabs";
    public static readonly CenterId: string = "wac-center";
    public static readonly EmptyId: string = "wac-choose";
    /** On `<html>` while the panes are on screen - every wide-layout rule in app.css hangs off it. */
    public static readonly HtmlClass: string = "wac-three";
    /** The list pane's landmark name. */
    public static readonly LeftLabel: string = "Chats";

    /** The two panes (the AI panel is the center's own right column). `Left` = the list's markup, `Tabs` the view tabs. */
    public static Shell = (Tabs: string, Left: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ THE LIST PANE IS NAVIGATION (audit 2026-10-01): a `<nav>` named "Chats" - it was an `<aside>`, which says
            // "beside the point" about the one list every route is reached from. The view tabs inside it are their own
            // `<nav>` ("Views", `AppMain.Tabs`), so a screen reader's landmark list tells the two apart.
            // ⭐ THE RAIL IS ITS OWN COLUMN (business look wave B, 2026-10-13): the views' `<nav>` (`AppRail`) sits in
            // `#wac-lefttabs` at the far LEFT of the shell, a sibling of the list pane - it was a row of tabs inside it.
            return "<div class=\"threecol\" id=\"" + AppThreeColView.RootId + "\"><div class=\"lefttabs\" id=\"" + AppThreeColView.TabsId + "\">"
                + Tabs + "</div><nav class=\"paneleft\" id=\"" + AppThreeColView.LeftId
                + "\" aria-label=\"" + AppThreeColView.LeftLabel + "\">" + Left
                + "</nav><div class=\"panecenter\" id=\"" + AppThreeColView.CenterId + "\"></div></div>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeColView.Shell");
            return "";
        }
    };

    /**
     * The center with no chat open: a calm "Choose a chat" and how to move with the keyboard, then `Below` (the Today
     * strip, "Ask about everyone", the task-close cards, the coverage line - whatever the chat list's screen carries).
     */
    public static Empty = (Mac: boolean, Below: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            // ⭐ OWNER 2026-11-01: no visible "Choose a chat" heading or keyboard sentence - the Today box is the screen. The
            // heading stays for a screen reader (the section is labelled by it); `Mac` is kept for the callers' signature.
            return "<section class=\"choose\" id=\"" + AppThreeColView.EmptyId + "\" aria-labelledby=\"wac-choose-h\">"
                + "<h1 id=\"wac-choose-h\" class=\"sr\">Choose a chat</h1></section>"
                + Below;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeColView.Empty");
            return "";
        }
    };

    private static Kbd = (Word: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return "<kbd>" + kHTMLCore.EscapeHtml(Word) + "</kbd>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeColView.Kbd");
            return "";
        }
    };

}

/** The little of an element the shell keeper touches - the browser's own, or a test's. */
export interface IThreeEl {
    innerHTML: string;
    scrollTop: number;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    removeAttribute(Name: string): void;
    querySelector(Selector: string): IThreeEl | null;
    querySelectorAll(Selector: string): ArrayLike<IThreeEl>;
    contains(Other: IThreeEl | null): boolean;
    focus(): void;
}

export interface IThreeDoc {
    getElementById(Id: string): IThreeEl | null;
    Active(): IThreeEl | null;
    HtmlClass(Name: string, On: boolean): void;
}

/** What the keeper asks the app for - markup only (the list, the tabs, one row's meta line) and "the list was drawn". */
export interface IThreeHost {
    TabsHtml(At: string): string;
    LeftHtml(Current: string): string;
    ListHtml(Current: string): string;
    MetaHtml(ChatId: string): string;
    ListDrawn(List: IThreeEl): void;
}

/** A list pane's scroll position and the row that had focus, across a redraw. */
export class AppPaneState {
    public Top: number = 0;
    public FocusChat: string = "";
}

/**
 * Keeps the panes: made once, then only the center and the tabs' line are rewritten, and the list only when it must be.
 * ⚠ ONE PER PAGE (`AppMain.Three`); `Leave` forgets the shell when a route paints the whole page (sign-in, a refusal).
 */
export class AppThreeCol {
    public static readonly ListId: string = "wac-chatlist";
    public static readonly CurrentAttr: string = "aria-current";

    private Sig: string = "";
    private SigIndex: object | null = null;
    /** How many times the list was drawn (made or redrawn) - the suite's proof that opening a chat does not. */
    public ListDraws: number = 0;

    /**
     * Show a route: make the shell if it is not there, light the route's tab, redraw the list only when its index, search
     * or mode changed (else mark `Current` in place), and put `Center` in the center. Answers the center, or null.
     */
    public Show = (Doc: IThreeDoc, Where: IThreeEl, Host: IThreeHost, Index: object, Sig: string, At: string, Current: string,
        Center: string): IThreeEl | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Doc.getElementById(AppThreeColView.RootId) === null) {
                Where.innerHTML = AppThreeColView.Shell(Host.TabsHtml(At), Host.LeftHtml(Current));
                this.Drawn(Doc, Host, Index, Sig);
            } else {
                this.Tabs(Doc, Host, At);
                if (Sig !== this.Sig || Index !== this.SigIndex) { this.RepaintList(Doc, Host, Index, Sig, Current); }
                else { this.MarkCurrent(Doc, Host, Current); }
            }
            Doc.HtmlClass(AppThreeColView.HtmlClass, true);
            const Mid: IThreeEl | null = Doc.getElementById(AppThreeColView.CenterId);
            if (Mid === null) { return null; }
            Mid.innerHTML = Center;
            Mid.scrollTop = 0;
            return Mid;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeCol.Show");
            return null;
        }
    };

    /** Is the shell on the page now? */
    public IsOn = (Doc: IThreeDoc): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return Doc.getElementById(AppThreeColView.RootId) !== null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeCol.IsOn");
            return false;
        }
    };

    /** The page is being painted whole (a narrow route, sign-in, a refusal): the wide rules off, the list forgotten. */
    public Leave = (Doc: IThreeDoc): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Doc.HtmlClass(AppThreeColView.HtmlClass, false);
            this.Sig = "";
            this.SigIndex = null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeCol.Leave");
        }
    };

    /**
     * The list redrawn (live move, search, rows/cards, a rebuilt index): the pane's scroll kept and focus put back on the
     * same row. ⛔ The center is never touched - the conversation, its scroll and the composer's caret stay.
     */
    public RepaintList = (Doc: IThreeDoc, Host: IThreeHost, Index: object, Sig: string, Current: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Pane: IThreeEl | null = Doc.getElementById(AppThreeColView.LeftId);
            const List: IThreeEl | null = Doc.getElementById(AppThreeCol.ListId);
            if (Pane === null) { return; }
            const Kept: AppPaneState = AppThreeCol.Capture(Doc, Pane);
            if (List !== null) { List.innerHTML = Host.ListHtml(Current); }
            // ⚠ The rail is not in the pane any more (it is the shell's own column), so the whole pane is the list's markup.
            else { Pane.innerHTML = Host.LeftHtml(Current); }
            this.Drawn(Doc, Host, Index, Sig);
            AppThreeCol.Restore(Pane, Kept);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeCol.RepaintList");
        }
    };

    /** The pane's scroll and the focused row's chat, before a redraw. */
    public static Capture = (Doc: IThreeDoc, Pane: IThreeEl): AppPaneState => {
        const Out: AppPaneState = new AppPaneState();
        if (getCoreUtils().KErrorMode === true) { return Out; }
        try {
            Out.Top = Pane.scrollTop;
            const Active: IThreeEl | null = Doc.Active();
            if (Active === null || Pane.contains(Active) === false) { return Out; }
            for (const Row of Array.from(Pane.querySelectorAll("li[data-chat]"))) {
                if (Row.contains(Active) === true) { Out.FocusChat = nz(Row.getAttribute("data-chat")); break; }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeCol.Capture");
            return Out;
        }
    };

    /** Scroll back where it was; focus back on the same row's link (if it is still listed). */
    public static Restore = (Pane: IThreeEl, Kept: AppPaneState): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            Pane.scrollTop = Kept.Top;
            if (Kept.FocusChat.length === 0) { return; }
            const Link: IThreeEl | null = AppThreeCol.LinkOf(Pane, Kept.FocusChat);
            if (Link !== null) { Link.focus(); }
            Pane.scrollTop = Kept.Top;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeCol.Restore");
        }
    };

    /** The row link of a chat in the pane, or null. ⚠ Compared by attribute, never put into a selector (ids carry "@" and "."). */
    public static LinkOf = (Pane: IThreeEl, Chat: string): IThreeEl | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            for (const Row of Array.from(Pane.querySelectorAll("li[data-chat]"))) {
                if (nz(Row.getAttribute("data-chat")) === Chat) { return Row.querySelector("a"); }
            }
            return null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeCol.LinkOf");
            return null;
        }
    };

    /**
     * The open chat's row: `aria-current="page"` (every other row loses it) and its meta line redrawn in place (opened =
     * read: the "N new" goes at once). ⭐ The link element itself is kept, so a row that had focus keeps it.
     */
    public MarkCurrent = (Doc: IThreeDoc, Host: IThreeHost, Current: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const List: IThreeEl | null = Doc.getElementById(AppThreeCol.ListId);
            if (List === null) { return; }
            for (const Row of Array.from(List.querySelectorAll("li[data-chat]"))) {
                const Link: IThreeEl | null = Row.querySelector("a");
                if (Link === null) { continue; }
                const Is: boolean = Current.length > 0 && nz(Row.getAttribute("data-chat")) === Current;
                if (Is === false) { if (Link.getAttribute(AppThreeCol.CurrentAttr) !== null) { Link.removeAttribute(AppThreeCol.CurrentAttr); } continue; }
                Link.setAttribute(AppThreeCol.CurrentAttr, "page");
                const Meta: IThreeEl | null = Link.querySelector(".m");
                const Fresh: string = Host.MetaHtml(Current);
                if (Meta !== null && Fresh.length > 0 && Meta.innerHTML !== Fresh) { Meta.innerHTML = Fresh; }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeCol.MarkCurrent");
        }
    };

    /** The tabs' line - only when it says something else (a keyboard user's focus on a tab survives an unchanged line). */
    private Tabs = (Doc: IThreeDoc, Host: IThreeHost, At: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Line: IThreeEl | null = Doc.getElementById(AppThreeColView.TabsId);
            if (Line === null) { return; }
            const Html: string = Host.TabsHtml(At);
            if (Line.innerHTML === Html) { return; }
            // ⭐ A TAB PRESSED WITH THE KEYBOARD KEEPS THE FOCUS (audit 2026-10-01): the line is rewritten because the lit
            // tab moved, and the link that was just pressed went with the old markup - the focus fell to the page.
            AppFocusKeep.Swap({ activeElement: Doc.Active() as unknown as IFocusEl | null, body: null },
                Line as unknown as IFocusEl & { innerHTML: string }, Html, false);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeCol.Tabs");
        }
    };

    private Drawn = (Doc: IThreeDoc, Host: IThreeHost, Index: object, Sig: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Sig = Sig;
            this.SigIndex = Index;
            this.ListDraws = this.ListDraws + 1;
            const List: IThreeEl | null = Doc.getElementById(AppThreeCol.ListId);
            const Pane: IThreeEl | null = Doc.getElementById(AppThreeColView.LeftId);
            if (List !== null) { Host.ListDrawn(List); } else if (Pane !== null) { Host.ListDrawn(Pane); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeCol.Drawn");
        }
    };
}

/** The browser's document as `IThreeDoc` (the class on `<html>` through `classList`). */
export class AppThreeDoc implements IThreeDoc {
    public getElementById = (Id: string): IThreeEl | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            return document.getElementById(Id) as unknown as IThreeEl | null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeDoc.getElementById");
            return null;
        }
    };

    public Active = (): IThreeEl | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            return document.activeElement as unknown as IThreeEl | null;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeDoc.Active");
            return null;
        }
    };

    public HtmlClass = (Name: string, On: boolean): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            document.documentElement.classList.toggle(Name, On === true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppThreeDoc.HtmlClass");
        }
    };
}
